'use client';

import { type PropsWithChildren, type ReactNode, useCallback, useEffect, useMemo } from 'react';
import type { SWRConfiguration } from 'swr';
import { getRequestInit } from '../../../../../common/util/services/authenticatedFetch';
import { useAuthenticatedJsonGet, useJsonGet } from '../../../../../common/util/services/useAuthenticatedJsonGet';
import { CommonError } from '../../../../../components/error/CommonError';
import { Spinner } from '../../../../../components/Spinner/Spinner';
import { LoadingContent } from '../loading/LoadingContent';

export interface FetchContentProps<T> extends PropsWithChildren {
	url?: string;
	options?: SWRConfiguration;
	setData?: (data: T | undefined) => void;
	setLoading?: (loading: boolean) => void;
	loadingContent?: ReactNode;
	noDataContent?: ReactNode;
	hasData?: boolean;
}

interface DataLoadingProps {
	message?: string;
}

function DataLoadingError({ message }: DataLoadingProps): ReactNode {
	const messageValue = message ?? 'Error loading data, click to retry';
	return <>{messageValue}</>;
}

// TODO: combined shared logic for these components
export function AuthenticatedJsonContent<T>({
	url,
	options,
	children,
	noDataContent,
	hasData,
	loadingContent,
	setData,
	setLoading,
}: FetchContentProps<T>): ReactNode {
	const [data, loading, error, mutate] = useAuthenticatedJsonGet<T>(url, options);

	// Don't consider as loading if we don't have a URL
	const componentLoading = url ? loading : false;

	useEffect(() => {
		setLoading?.(componentLoading);
	}, [componentLoading, setLoading]);

	// Call to update data when it changes
	useEffect(() => {
		// Don't continue to update if we no longer have a URL
		if (!url) {
			return;
		}
		setData?.(data);
	}, [url, data, setData]);

	if (error) {
		return <CommonError error={error} reset={mutate} tooltipContent={<DataLoadingError />} />;
	}

	let componentLoadingContent = loadingContent;
	if (componentLoadingContent === undefined) {
		componentLoadingContent = <Spinner />;
	}

	return (
		<LoadingContent
			loading={componentLoading}
			data={data}
			noDataContent={noDataContent}
			hasData={hasData}
			loadingContent={componentLoadingContent}
		>
			{children}
		</LoadingContent>
	);
}

export function FetchJsonContent<T>(props: FetchContentProps<T>): ReactNode {
	const url = props.url;

	const parentSetData = props.setData;
	const setData = useCallback(
		(newData: T | undefined) => {
			if (parentSetData) {
				parentSetData(newData);
			}
		},
		[parentSetData],
	);

	const parentSetLoading = props.setLoading;
	const setLoading = useCallback(
		(newLoading: boolean) => {
			if (parentSetLoading) {
				parentSetLoading(newLoading);
			}
		},
		[parentSetLoading],
	);

	const requestInit = useMemo(() => {
		return getRequestInit();
	}, []);
	const [data, loading, error, mutate] = useJsonGet<T>(url, requestInit);

	useEffect(() => {
		setLoading(loading);
	}, [loading, setLoading]);

	useEffect(() => {
		setData(data);
	}, [data, setData]);

	if (error) {
		return <CommonError error={error} reset={mutate} tooltipContent={<DataLoadingError />} />;
	}

	let loadingContent = props.loadingContent;
	if (loadingContent === undefined) {
		loadingContent = <Spinner />;
	}

	return (
		<LoadingContent
			loading={loading}
			data={data}
			noDataContent={props.noDataContent}
			hasData={props.hasData}
			loadingContent={loadingContent}
		>
			{props.children}
		</LoadingContent>
	);
}
